import type { UnifiedCanvas } from '../modes/unified/UnifiedCanvas';
import type { ProjectStorage } from '../storage/ProjectStorage';
import type { ProjectManager } from '../ui/ProjectManager';
import type { SessionController } from './SessionController';
import type { AppMode } from '../types';

export interface ProjectContext {
  getStorage: () => ProjectStorage | null;
  getMode: () => AppMode;
  getUnifiedCanvas: () => UnifiedCanvas | null;
  getProjectManager: () => ProjectManager | null;
  getInvertedFromTheme: () => boolean;
  loadCurrentModeState: () => void;
  switchMode: (mode: AppMode, preserveCurrentState?: boolean) => void;
  getCurrentProjectId: () => string | null;
  setCurrentProjectId: (id: string | null) => void;
  getCurrentProjectName: () => string;
  setCurrentProjectName: (name: string) => void;
  getUnifiedState: () => string | null;
  setUnifiedState: (state: string | null) => void;
  refreshProjectList: () => Promise<void>;
}

export class ProjectController {
  private readonly context: ProjectContext;
  private readonly sessions: SessionController;
  private activeOperation: Promise<unknown> = Promise.resolve();


  constructor(context: ProjectContext, sessions: SessionController) {
    this.context = context;
    this.sessions = sessions;
  }

  private async enqueue<T>(op: () => Promise<T>): Promise<T> {
    const next = this.activeOperation.then(op);
    this.activeOperation = next.catch(() => { });
    return next;
  }


  async handleNewProject(): Promise<void> {
    return this.enqueue(async () => {
      this.sessions.cancelAutoSave();

      await this.sessions.performAutoSave(false);

      this.sessions.setLoading(true);

      try {
        await this.resetAppState();
        this.context.setCurrentProjectId(null);
        this.context.setCurrentProjectName(this.sessions.generateSessionName());
        await this.createEmptySession();
      } finally {
        this.sessions.setLoading(false);
      }
    });
  }


  async handleLoadProject(id: string): Promise<void> {
    return this.enqueue(async () => {
      const storage = this.context.getStorage();

      if (!storage) return;

      if (id === this.context.getCurrentProjectId()) return;

      this.sessions.cancelAutoSave();
      await this.sessions.performAutoSave(false);
      this.sessions.setLoading(true);

      try {
        const project = await storage.getProject(id);
        if (!project) return;

        // Unified state: prefer drawModeState (where unified state is stored),
        // but merge legacy diagramModeState for backward compat
        const unifiedState = this.mergeProjectStates(project.drawModeState ?? null, project.diagramModeState ?? null);
        this.context.setUnifiedState(unifiedState);
        this.sessions.setLastSavedStates(unifiedState, null);

        this.context.setCurrentProjectId(project.id);
        this.context.setCurrentProjectName(project.name);

        const targetMode = project.lastMode ?? 'draw';
        if (targetMode !== this.context.getMode()) {
          // Pass false to prevent overwriting the just-loaded project's mode states
          this.context.switchMode(targetMode, false);
        }
        // Always restore state (including tool) after mode is set
        this.context.loadCurrentModeState();

        this.context.getProjectManager()?.setCurrentProject(project.id, project.name);
      } finally {
        this.sessions.setLoading(false);
      }
    });
  }


  async handleDeleteProject(id: string): Promise<void> {
    return this.enqueue(async () => {
      const storage = this.context.getStorage();

      if (!storage) return;

      this.sessions.cancelAutoSave();
      await this.sessions.performAutoSave(false);
      await storage.deleteProject(id);


      if (this.context.getCurrentProjectId() === id) {
        this.sessions.cancelAutoSave();
        this.sessions.setLoading(true);

        try {
          await this.resetAppState();
          this.context.setCurrentProjectId(null);
          this.context.setCurrentProjectName('Untitled');
          this.context.getProjectManager()?.setCurrentProject(null, 'Untitled');

          this.sessions.clearLastSession();
        } finally {
          this.sessions.setLoading(false);
        }
      }

      await this.refreshProjectList();
    });
  }


  async handleRenameProject(id: string, newName: string): Promise<void> {
    return this.enqueue(async () => {
      const storage = this.context.getStorage();

      if (!storage) return;

      this.sessions.cancelAutoSave();
      await this.sessions.performAutoSave(false);
      await storage.renameProject(id, newName);


      if (this.context.getCurrentProjectId() === id) {
        this.context.setCurrentProjectName(newName);
        this.context.getProjectManager()?.setCurrentProject(id, newName);
      }

      await this.refreshProjectList();
    });
  }


  async refreshProjectList(): Promise<void> {
    const storage = this.context.getStorage();
    const projectManager = this.context.getProjectManager();
    if (!storage || !projectManager) return;

    const projects = await storage.listProjects();
    projectManager.updateProjects(projects);
  }

  private async createEmptySession(): Promise<void> {
    const storage = this.context.getStorage();
    if (!storage) return;

    const thumbnail = this.sessions.createEmptyThumbnail();
    const canvas = this.context.getUnifiedCanvas();
    const config = canvas?.getConfig() ?? {
      width: 1920,
      height: 1080,
      backgroundColor: '#ffffff',
    };

    const project = await storage.saveProject(
      null,
      this.context.getCurrentProjectName(),
      thumbnail,
      config,
      undefined,
      undefined,
      this.context.getMode()
    );

    this.context.setCurrentProjectId(project.id);
    this.context.setCurrentProjectName(project.name);
    this.sessions.resetLastSavedStates();
    this.context.getProjectManager()?.setCurrentProject(project.id, project.name);
    await this.refreshProjectList();
  }

  private async resetAppState(): Promise<void> {
    this.context.setUnifiedState(null);
    this.sessions.resetLastSavedStates();

    const canvas = this.context.getUnifiedCanvas();
    if (canvas) {
      canvas.clear();
    }
  }

  /**
   * Merge legacy separate draw/diagram states into a single unified state.
   * If the drawModeState already contains all element types (unified format), use it directly.
   * Otherwise merge draw strokes with diagram shapes/connectors/text.
   */
  private mergeProjectStates(drawState: string | null, diagramState: string | null): string | null {
    if (!drawState && !diagramState) return null;

    // If only one exists, use it
    if (!diagramState) return drawState;
    if (!drawState) return diagramState;

    // Check if drawState already has diagram elements (unified format)
    try {
      const draw = JSON.parse(drawState);
      if (draw.shapes?.length > 0 || draw.connectors?.length > 0 || draw.drawShapes?.length > 0) {
        // Already unified format
        return drawState;
      }

      // Legacy format: merge draw strokes into diagram state
      const diagram = JSON.parse(diagramState);
      return JSON.stringify({
        ...diagram,
        strokes: draw.strokes ?? [],
        drawShapes: draw.drawShapes ?? [],
        // Keep draw tool settings
        currentColor: draw.currentColor ?? diagram.currentColor,
        currentWidth: draw.currentWidth ?? 8,
      });
    } catch {
      return drawState ?? diagramState;
    }
  }
}
